import { ref, get, update } from "firebase/database";
import { database } from "./firebase-config.js";
import { MARKETING_CONSENT_VERSION } from "./utils/marketingConsent.js";

export const checkIfBusinessIsActive = async (businessId) => {
  try {
    const businessRef = ref(database, `businesses/${businessId}/isActive`);
    const snapshot = await get(businessRef);

    if (snapshot.exists()) {
      return snapshot.val();
    } else {
      return false;
    }
  } catch (error) {
    return false;
  }
};

export const checkBusinessIdExists = async (businessId) => {
  try {
    const businessRef = ref(database, `businesses/${businessId}`);
    const snapshot = await get(businessRef);
    return snapshot.exists();
  } catch (error) {
    console.error("Error checking business ID:", error);
    throw error;
  }
};

export const getWebDisplayDataForBusiness = async (businessId) => {
  try {
    const businessRef = ref(
      database,
      `businesses/${businessId}/webDisplayData`
    );
    const snapshot = await get(businessRef);

    if (snapshot.exists()) {
      return snapshot.val();
    } else {
      throw new Error("Business not found");
    }
  } catch (error) {
    console.error("Error fetching business display data:", error);
    throw error;
  }
};

// Reads only the three fields enrolment needs, in parallel, instead of the
// whole `businesses/<id>` node - which also carries every customer, stamp and
// redeem the business has ever recorded and keeps growing.
export const getBusinessData = async (businessId) => {
  try {
    const field = (name) =>
      get(ref(database, `businesses/${businessId}/${name}`));
    const [rewardsCardSnap, thresholdSnap, startStampSnap] = await Promise.all([
      field("rewardsCardData"),
      field("thresholdForRedeeming"),
      // Free stamps the business hands new sign-ups. The node only exists
      // once a business has changed it away from the default, so absent = 0.
      field("startStampCount"),
    ]);

    if (
      !rewardsCardSnap.exists() &&
      !thresholdSnap.exists() &&
      !startStampSnap.exists()
    ) {
      throw new Error("Business not found");
    }

    const valueOrUndefined = (snap) => (snap.exists() ? snap.val() : undefined);
    return {
      businessData: valueOrUndefined(rewardsCardSnap),
      thresholdForRedeeming: valueOrUndefined(thresholdSnap),
      startStampCount: valueOrUndefined(startStampSnap),
    };
  } catch (error) {
    console.error("Error fetching business data:", error);
    throw error;
  }
};

// Stamps a new customer is credited with on sign-up. Anything unusable (absent
// node, non-numeric, negative) means "no head start", and the count can never
// exceed a full card - a business could otherwise lower its threshold after
// setting a larger start count and enrol customers past their own goal.
const resolveStartingPoints = (startStampCount, thresholdForRedeeming) => {
  const start = Math.floor(Number(startStampCount));
  if (!Number.isFinite(start) || start <= 0) return 0;
  const threshold = Number(thresholdForRedeeming);
  if (!Number.isFinite(threshold) || threshold <= 0) return 0;
  return Math.min(start, threshold);
};

// NOTE: Need to replace '.' with another unique character to enable saving as a key in DB
const sanitiseEmailKey = (email) => email.replace(/\./g, "&");

const customerForBusinessPath = (email, businessId) =>
  `customers/${sanitiseEmailKey(email)}/businessesRegisteredWith/${businessId}`;

// Whether this customer already holds a card with the business. Defaults to
// false on a read error, exactly as the previous inline check did, so an
// enrolment is never blocked by a flaky read.
export const isCustomerRegisteredWithBusiness = async (email, businessId) => {
  try {
    const snapshot = await get(
      ref(database, customerForBusinessPath(email, businessId))
    );
    return snapshot.exists();
  } catch (error) {
    console.error("Error checking path:", error);
    // NOTE: Defaults to false if facing error.
    return false;
  }
};

export const saveNewCustomerToDatabase = async (
  email,
  businessId,
  thresholdForRedeeming,
  startStampCount = 0,
  options = {}
) => {
  const emailAddress = sanitiseEmailKey(email);

  // NOTE: Should not override data if customer email for business already
  // exists. A caller that has already run the check (the enrolment flow runs
  // it in parallel with the business reads) passes the answer in; otherwise it
  // is checked here as before.
  const alreadyRegistered =
    typeof options.alreadyRegistered === "boolean"
      ? options.alreadyRegistered
      : await isCustomerRegisteredWithBusiness(email, businessId);
  if (alreadyRegistered) {
    return;
  }
  const isoTimestamp = new Date().toISOString();

  // Marketing consent is its own unticked box on the form, so it is only ever
  // true when the customer opted in - never assumed. The version and time
  // record which wording they saw and when, which is what a business needs if
  // it is ever asked to prove consent.
  const marketingConsent = Boolean(options.marketingConsent);

  // Free stamps count toward the reward exactly like scanned ones, so they seed
  // BOTH balances: currentPoints (what the card shows) and totalHistoricPoints
  // (lifetime stamps, which the apps divide by the threshold to count rewards
  // earned - seeding only currentPoints would under-report that forever).
  const startingPoints = resolveStartingPoints(
    startStampCount,
    thresholdForRedeeming
  );

  // Both records are written in ONE multi-path update: a single round trip,
  // and atomic, so a customer can no longer end up with a card record but no
  // entry under the business (or vice versa) if the second of two writes
  // failed. Security rules are evaluated per path exactly as they were for the
  // two separate writes.
  //
  // Business node: the mirrored `eligibleToRedeemNow` flag is deliberately not
  // written there - the RTDB rules reserve that field for employees, and a
  // customer enrolling is not one.
  await update(ref(database), {
    [customerForBusinessPath(email, businessId)]: {
      totalHistoricPoints: startingPoints,
      currentPoints: startingPoints,
      eligibleToRedeemNow:
        startingPoints > 0 && startingPoints >= Number(thresholdForRedeeming),
      registeredForRewardsOn: isoTimestamp,
      thresholdForRedeeming: thresholdForRedeeming,
      // `allowMarketingEmails` is the flag the iOS operator app reads and
      // writes, so the key name must not change.
      allowMarketingEmails: marketingConsent,
      marketingConsentVersion: MARKETING_CONSENT_VERSION,
      marketingConsentAt: isoTimestamp,
    },
    [`businesses/${businessId}/customers/${emailAddress}`]: {
      registeredOn: isoTimestamp,
    },
  });
};
